import {
    Colors,
    Icon,
    MultiSelectGlass,
    type OptionGroup,
    type OptionProps,
} from '@amelio/ui-library';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';

import { useFollowedSelection } from './useFollowedSelection';

import type { Company } from '@/types/directory/Company';

import { useAmelioTranslations } from '@/hooks';
import {
    useCompanyAttributesFilters,
    type SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import { AttributeSelectList } from '@/pages/CycleEvaluation/DefineAudience/components/AttributeSelectList/AttributeSelectList';
import { getCompanyAttributeList } from '@/utils/company';
import { familiesSeededBy } from '@/utils/companyAttributeFilters';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';

export type CompanyAttributeFiltersHandle = {
    reset: () => void;
};

/**
 * A second group of values the same menu offers, under its own header, beside the attribute
 * families. An attribute family opens its own value list under the bar; one of these values
 * filters on the spot, and the caller owns both the selection and what it means.
 */
export type FilterValueGroup = {
    label: string;
    options: OptionProps[];
    selectedValues: string[];
    onToggle: (value: string) => void;
};

/** Every base company-attribute family `getCompanyAttributeList` always returns, regardless of
 * which custom attributes the company has configured. */
export const COMPANY_ATTRIBUTE_KINDS = [
    'departments',
    'jobTitles',
    'companyAddresses',
    'employeeLevels',
    'ageGroups',
    'tenures',
    'managers',
] as const;

export type CompanyAttributeKind = (typeof COMPANY_ATTRIBUTE_KINDS)[number];

// Historical default, kept so every existing caller sees the exact same dropdown as before this
// component gained the `attributes` prop: every base family except age group and tenure, which
// were excluded because they are not relevant to roster/employee-picker filtering.
const DEFAULT_ATTRIBUTES: CompanyAttributeKind[] = COMPANY_ATTRIBUTE_KINDS.filter(
    (kind) => kind !== 'ageGroups' && kind !== 'tenures',
);

// Where the selection comes from: the caller holds it, or the picker holds its own. Never both,
// so which one wins is never a question.
type SelectionSource =
    | {
          // The selection shown, for a caller that holds it (in the address, for instance): the
          // picker follows it whenever it changes, opening the families it fills, and announces a
          // pick for the caller to write back -- never the selection it was just handed.
          selection: SelectedFilters;
          defaultFilters?: never;
      }
    | {
          selection?: never;
          // Selection the picker opens with, for a caller that reopens something already filtered
          // (a saved envelope audience, a stored view). Read once, when this component mounts: give
          // it a new `key` to seed it again. The attribute families it opens are the ones this
          // selection actually fills, so the chips are visible and not just held in state. Left
          // unset, the picker opens empty.
          defaultFilters?: SelectedFilters;
      };

type CompanyAttributeFiltersProps = SelectionSource & {
    company?: Company;
    onFiltersChange: (filters: SelectedFilters) => void;
    // When true, renders only the filter button inline; selected attribute filters
    // wrap below on their own row. Useful when placed beside a search bar.
    inline?: boolean;
    // Which base attribute families the dropdown offers. Custom company attributes are always
    // offered, regardless of this list. Defaults to every family except age group and tenure.
    // Readonly so a caller wanting them all can pass COMPANY_ATTRIBUTE_KINDS itself — one stable
    // identity, which the option list below memoises on — instead of a fresh copy each render.
    attributes?: readonly CompanyAttributeKind[];
    // Where the dropdown opens relative to its trigger. Left unset, the menu opens below and
    // left-aligned; a caller placing the filter low in a modal asks for a corner that stays inside.
    placement?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
    // A second group of values offered under its own header, for a caller that filters on
    // something other than who the employee is. Left unset, the menu stays the single flat list
    // of attribute families it has always been.
    valueGroup?: FilterValueGroup;
};

// Reuses the app's company-attribute filter system (base + custom): a glassy "Filter by
// attribute" MultiSelectGlass that reveals one AttributeSelectList per chosen
// attribute. The selected `filters` are lifted to the page, which maps them to whatever id
// arrays its own query needs (departmentIds / jobTitleIds / managerIds / …).
export const CompanyAttributeFilters = forwardRef<
    CompanyAttributeFiltersHandle,
    CompanyAttributeFiltersProps
>(
    (
        {
            company,
            onFiltersChange,
            inline = false,
            attributes = DEFAULT_ATTRIBUTES,
            defaultFilters,
            selection,
            placement,
            valueGroup,
        },
        ref,
    ) => {
        const { t, i18n } = useAmelioTranslations();

        // The selection the picker opens with, and the families it fills: without them the seeded
        // values would live in state with nothing on screen showing them. Read once, at mount.
        const [initialSelection] = useState(() => {
            const seed = selection ?? defaultFilters;

            return (
                seed && {
                    filters: seed,
                    families: familiesSeededBy(getCompanyAttributeList(company), seed),
                }
            );
        });

        const {
            filters,
            selectedCompanyAttributes,
            onSelectCompanyAttribute,
            onSelectCompanyAttributeOption,
            resetFilters,
            setFilters,
            setSelectedCompanyAttributes,
        } = useCompanyAttributesFilters({
            company,
            defaultFilters: initialSelection?.filters,
            defaultSelectedAttributes: initialSelection?.families,
        });

        // The page rebuilds its own query's filter params from what this announces.
        useFollowedSelection({
            selection,
            company,
            filters,
            setFilters,
            setSelectedCompanyAttributes,
            onFiltersChange,
        });

        useImperativeHandle(ref, () => ({
            reset: resetFilters,
        }));

        const companyAttributeOptions = useMemo<OptionProps[]>(() => {
            const allAttributes = getCompanyAttributeList(company);
            const visibleKinds = new Set<string>(attributes);
            // A key outside COMPANY_ATTRIBUTE_KINDS is a custom company attribute (keyed by its own
            // id), always offered; only the base families are gated by the `attributes` prop.
            const baseKinds: readonly string[] = COMPANY_ATTRIBUTE_KINDS;

            return Object.entries(allAttributes)
                .filter(([key]) => !baseKinds.includes(key) || visibleKinds.has(key))
                .map(([, attr]) => ({
                    label: decodeAndSanitize(attr.name),
                    value: attr.name,
                }));
        }, [company, i18n.language, attributes]);

        const selectedCompanyAttributeOptions = Object.values(selectedCompanyAttributes ?? {});

        // Two meanings share one value space once a second group is offered. The attribute
        // families answer first, so a caller's value can never shadow one of them.
        const handleSelect = (value: string) => {
            if (companyAttributeOptions.some((option) => option.value === value)) {
                onSelectCompanyAttribute(value);

                return;
            }

            valueGroup?.onToggle(value);
        };

        const menuGroups = useMemo<OptionGroup[] | undefined>(() => {
            if (!valueGroup) {
                return undefined;
            }

            return [
                {
                    id: 'company-attributes',
                    label: t('common.employeeAttribute'),
                    options: companyAttributeOptions,
                },
                { id: 'values', label: valueGroup.label, options: valueGroup.options },
            ];
        }, [companyAttributeOptions, t, valueGroup]);

        const filterButton = (
            <div className={'shrink-0'}>
                <MultiSelectGlass
                    sort={'none'}
                    placeholder={t('common.filter')}
                    placement={placement}
                    icon={
                        <Icon
                            name={'essential-filter'}
                            color={Colors.BASE_600}
                        />
                    }
                    options={menuGroups ? undefined : companyAttributeOptions}
                    groups={menuGroups}
                    onChange={handleSelect}
                    values={[
                        ...selectedCompanyAttributeOptions.map((attribute) =>
                            decodeAndSanitize(attribute.name),
                        ),
                        ...(valueGroup?.selectedValues ?? []),
                    ]}
                />
            </div>
        );

        const attributeSelectors = selectedCompanyAttributeOptions.map((attribute) => (
            <AttributeSelectList
                key={attribute.name}
                attribute={attribute}
                filters={filters}
                selectCompanyAttributeOption={onSelectCompanyAttributeOption}
            />
        ));

        if (inline) {
            return (
                <>
                    {filterButton}
                    {selectedCompanyAttributeOptions.length > 0 && (
                        <div className={'col-span-full flex flex-wrap items-start gap-3'}>
                            {attributeSelectors}
                        </div>
                    )}
                </>
            );
        }

        return (
            <div className={'flex flex-wrap items-start gap-3'}>
                {filterButton}
                {attributeSelectors}
            </div>
        );
    },
);

CompanyAttributeFilters.displayName = 'CompanyAttributeFilters';
